import { useEffect, useRef } from 'react';
import {
  logScreenerAbandoned,
  AbandonStep,
  SCREENER_STARTED_AT_KEY,
} from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getWithExpiry } from '../utils/sessionStorage';

interface UseScreenerAbandonTrackerOptions {
  lastStep: AbandonStep;
  questionNumber?: number;
  isCompleted?: boolean;
}

export const useScreenerAbandonTracker = ({
  lastStep,
  questionNumber,
  isCompleted = false,
}: UseScreenerAbandonTrackerOptions): void => {
  const hasFiredRef = useRef(false);
  const lastStepRef = useRef(lastStep);
  const questionNumberRef = useRef(questionNumber);
  const isCompletedRef = useRef(isCompleted);

  useEffect(() => {
    lastStepRef.current = lastStep;
  }, [lastStep]);

  useEffect(() => {
    questionNumberRef.current = questionNumber;
  }, [questionNumber]);

  useEffect(() => {
    isCompletedRef.current = isCompleted;
  }, [isCompleted]);

  useEffect(() => {
    const fireAbandonEvent = () => {
      if (hasFiredRef.current || isCompletedRef.current) return;
      hasFiredRef.current = true;

      const applicantGuid = getWithExpiry('applicantGuid') || undefined;
      const jobId = getWithExpiry('jobId') || undefined;
      const companyId = getWithExpiry('companyId') || undefined;
      const startedAtRaw = getWithExpiry(SCREENER_STARTED_AT_KEY);
      const startedAt = typeof startedAtRaw === 'number'
        ? startedAtRaw
        : typeof startedAtRaw === 'string'
          ? parseInt(startedAtRaw, 10)
          : null;
      const secondsOnPage = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

      logScreenerAbandoned({
        jobId,
        companyId,
        applicantGuid,
        deviceType: getDeviceType(),
        lastStep: lastStepRef.current,
        questionNumber: questionNumberRef.current,
        secondsOnPage,
      });
    };

    const handleBeforeUnload = () => {
      fireAbandonEvent();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        fireAbandonEvent();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
